import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { AssetCreateUpdateRequest, AssetResponse } from "@switcherstudio/api-core-client";
import { AppDispatch } from "store/store";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import rollbar from "helpers/rollbar";
import { assembleV2PutOptions, uploadV2AssetToStorage } from "helpers/assetUploadHelpersV2";

interface UseCreateAssetAndUploadProps {
    createNewAssets: (
        updatedArgs: [keepPreviousVersions?: boolean, body?: AssetCreateUpdateRequest[]]
    ) => Promise<any>;
    onUploadStart?: () => void;
    onUploadProgress?: (fileId: string, progress: number) => void;
    onUploadSuccess?: (fileId: string) => void;
    onUploadError?: (fileId: string, error: string) => void;
    onUploadComplete?: () => void;
}

export const useCreateAssetAndUpload = ({
    createNewAssets,
    onUploadStart,
    onUploadProgress,
    onUploadSuccess,
    onUploadError,
    onUploadComplete
}: UseCreateAssetAndUploadProps) => {
    const dispatch = useDispatch<AppDispatch>();

    // Since Azure requires direct uploads using PUT via signed URLs and TUS does not allow PUT,
    // we implement a custom upload function straight to the Azure buckets
    const uploadFilesWithSignedUrls = useCallback(
        async (files: any[], fileAttributes: { [fileName: string]: any }) => {
            onUploadStart?.();

            let numberSuccessful = 0;
            let numberFailed = 0;

            try {
                // Step 1: Create asset records and get signed URLs
                // We ping our API with all the metadata required to provision an asset
                // In the response, we get back signed URLs for each asset to upload directly to Azure
                const { assetV2PutOptions, filesV2WithMetadata } =
                    await assembleV2PutOptions(files, fileAttributes);

                const assetResponses: AssetResponse[] = await createNewAssets([
                    false,
                    assetV2PutOptions
                ]);

                // 1) Upload ONLY metadata assets (.mmart/.mmsrc)
                // Upload assets to Azure blob storage
                await Promise.all(
                    assetResponses.map(async (asset) => {
                        const candidate = filesV2WithMetadata.find(
                            (f) => f.imageMid === asset.Details.Id
                        );
                        if (!candidate) return;

                        await uploadV2AssetToStorage(candidate, asset);
                    })
                );

                // 2) Upload ONLY raw assets (mid)
                // Upload each file directly to Azure
                for (const file of files) {
                    const candidate = filesV2WithMetadata.find(
                        (f) => f.uppyId === file.id
                    );

                    if (!candidate) {
                        rollbar.error(
                            "No candidate found for file during raw upload",
                            {
                                fileId: file.id,
                                fileName: file.name
                            }
                        );
                        numberFailed++;
                        onUploadError?.(file.id, "Missing upload mapping for file");
                        continue;
                    }

                    const rawAsset = assetResponses.find(
                        (a) => a.Details.Id === candidate.mid
                    );

                    const signedUrl =
                        rawAsset?.Details?.SignedUrl || rawAsset?.Details?.Url;

                    if (!signedUrl) {
                        rollbar.error("Signed URL missing for raw asset upload", {
                            assetId: rawAsset?.Details?.Id,
                            fileId: file.id
                        });
                        numberFailed++;
                        onUploadError?.(file.id, "Signed URL missing");
                        continue;
                    }

                    try {
                        onUploadProgress?.(file.id, 0);

                        // Upload directly to Azure using fetch with PUT
                        const uploadResponse = await fetch(signedUrl, {
                            method: "PUT",
                            body: file.data, // Raw file data
                            headers: {
                                "x-ms-blob-type": "BlockBlob",
                                "Content-Type":
                                    file.type || "application/octet-stream"
                            }
                        });

                        if (!uploadResponse.ok) {
                            const errorText = await uploadResponse.text();
                            rollbar.error(
                                `Azure raw upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`
                            );
                            numberFailed++;
                            onUploadError?.(file.id, "Azure upload failed");
                            continue;
                        }

                        onUploadProgress?.(file.id, 100);
                        onUploadSuccess?.(file.id);
                        numberSuccessful++;
                    } catch (error) {
                        numberFailed++;
                        onUploadError?.(
                            file.id,
                            error instanceof Error
                                ? error.message
                                : "File upload failed"
                        );
                    }
                }

                if (numberSuccessful > 0) {
                    dispatch(
                        addNotification({
                            type: NotificationType.Success,
                            message:
                                numberSuccessful === 1
                                    ? "messages:asset-upload-success"
                                    : "messages:asset-upload-success-plural"
                        })
                    );
                }

                if (numberFailed > 0) {
                    dispatch(
                        addNotification({
                            type: NotificationType.Danger,
                            message:
                                numberFailed === 1
                                    ? "messages:asset-upload-failed"
                                    : "messages:asset-upload-failed-plural"
                        })
                    );
                }

                onUploadComplete?.();
            } catch (error) {
                files.forEach((file) => {
                    onUploadError?.(
                        file.id,
                        error instanceof Error
                            ? error.message
                            : "Asset creation failed"
                    );
                });
            }
        },
        [
            createNewAssets,
            dispatch,
            onUploadStart,
            onUploadProgress,
            onUploadSuccess,
            onUploadError,
            onUploadComplete
        ]
    );

    return { uploadFilesWithSignedUrls };
};
